// ============================================================
// Aggregator — KPIs, Perioden, Gruppierungen, Sorten
// ============================================================

import type {
  ArtRow,
  Eintrag,
  GroupMode,
  GroupRow,
  Kpi,
  MonthRow,
  Period,
  PeriodMode,
  SeriesResult,
  Spielart,
} from './types';
import { isoDay, MON, NOW, WD } from './format';

// ---------- KPI ----------
export function kpi(entries: Eintrag[]): Kpi {
  let einsatz = 0;
  let gewinn = 0;
  let payCount = 0;
  let netPos = 0;
  let maxG = 0;
  let maxN = -Infinity;
  let minN = Infinity;

  for (const e of entries) {
    einsatz += e.einsatz;
    gewinn += e.gewinn;
    if (e.gewinn > 0) payCount++;
    const net = e.gewinn - e.einsatz;
    if (net > e.einsatz) netPos++;
    if (e.gewinn > maxG) maxG = e.gewinn;
    if (net > maxN) maxN = net;
    if (net < minN) minN = net;
  }

  const n = entries.length;
  return {
    n,
    einsatz,
    gewinn,
    netto: gewinn - einsatz,
    rueckfluss: einsatz > 0 ? (gewinn / einsatz) * 100 : null,
    auszahlungsquote: n ? (payCount / n) * 100 : null,
    nettogewinnquote: n ? (netPos / n) * 100 : null,
    payCount,
    netPos,
    avgEinsatz: n ? einsatz / n : 0,
    avgGewinn: n ? gewinn / n : 0,
    maxG,
    maxN: n ? maxN : 0,
    minN: n ? minN : 0,
  };
}

// ---------- Period ----------
export function dataBounds(entries: Eintrag[]): { min: Date; max: Date } {
  if (!entries.length) return { min: NOW, max: NOW };
  let min = entries[0].iso;
  let max = entries[0].iso;
  for (const x of entries) {
    if (x.iso < min) min = x.iso;
    if (x.iso > max) max = x.iso;
  }
  return { min: new Date(min), max: new Date(max) };
}

export function period(
  mode: PeriodMode,
  work: Eintrag[],
  customFrom: string,
  customTo: string,
): Period {
  const b = dataBounds(work);
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
  const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);
  const days = (n: number) => ({
    from: startOfDay(new Date(NOW.getTime() - (n - 1) * 86400000)),
    to: endOfDay(NOW),
    rolling: true,
  });

  switch (mode) {
    case 'gesamt':
      return { from: b.min, to: b.max, label: 'Gesamtzeitraum', full: true };
    case 'curMonth':
      return {
        from: new Date(NOW.getFullYear(), NOW.getMonth(), 1),
        to: new Date(NOW.getFullYear(), NOW.getMonth() + 1, 0, 23, 59, 59),
        label: 'Aktueller Kalendermonat',
        cal: 'M',
      };
    case 'lastMonth':
      return {
        from: new Date(NOW.getFullYear(), NOW.getMonth() - 1, 1),
        to: new Date(NOW.getFullYear(), NOW.getMonth(), 0, 23, 59, 59),
        label: 'Letzter Kalendermonat',
        cal: 'M',
      };
    case 'curYear':
      return {
        from: new Date(NOW.getFullYear(), 0, 1),
        to: new Date(NOW.getFullYear(), 11, 31, 23, 59, 59),
        label: 'Aktuelles Kalenderjahr',
        cal: 'Y',
      };
    case 'lastYear':
      return {
        from: new Date(NOW.getFullYear() - 1, 0, 1),
        to: new Date(NOW.getFullYear() - 1, 11, 31, 23, 59, 59),
        label: 'Letztes Kalenderjahr',
        cal: 'Y',
      };
    case 'd30':
      return { ...days(30), label: 'Letzte 30 Tage' };
    case 'd90':
      return { ...days(90), label: 'Letzte 90 Tage' };
    case 'd365':
      return { ...days(365), label: 'Letzte 365 Tage' };
    case 'custom': {
      const f = customFrom ? startOfDay(new Date(customFrom)) : b.min;
      const t = customTo ? endOfDay(new Date(customTo)) : b.max;
      return { from: f, to: t, label: 'Benutzerdefiniert', custom: true };
    }
  }
}

export function prevPeriod(p: Period): Period | null {
  if (p.full) return null;
  if (p.cal === 'M') {
    const f = new Date(p.from.getFullYear(), p.from.getMonth() - 1, 1);
    const t = new Date(p.from.getFullYear(), p.from.getMonth(), 0, 23, 59, 59);
    return { from: f, to: t, label: `${MON[f.getMonth()]} ${f.getFullYear()}` };
  }
  if (p.cal === 'Y') {
    const f = new Date(p.from.getFullYear() - 1, 0, 1);
    const t = new Date(p.from.getFullYear() - 1, 11, 31, 23, 59, 59);
    return { from: f, to: t, label: `Jahr ${f.getFullYear()}` };
  }
  const len = p.to.getTime() - p.from.getTime();
  const t = new Date(p.from.getTime() - 1);
  const f = new Date(p.from.getTime() - 1 - len);
  const dstr = (d: Date) =>
    `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
  return { from: f, to: t, label: `${dstr(f)} – ${dstr(t)}` };
}

export function inRange(e: Eintrag, p: Period): boolean {
  const t = new Date(e.iso).getTime();
  return t >= p.from.getTime() && t <= p.to.getTime();
}

// Anzahl Monate, über die ein Zeitraum reicht (Basis für "Ø pro Monat").
// Kalenderzeiträume zählen angebrochene Kalendermonate bis heute, rollierende Zeiträume rechnen tagesgenau.
export function monthSpan(p: Period): number {
  const to = p.to.getTime() > NOW.getTime() ? NOW : p.to;
  if (to.getTime() < p.from.getTime()) return 0;
  if (p.rolling) return (to.getTime() - p.from.getTime()) / (86400000 * (365.25 / 12));
  return to.getFullYear() * 12 + to.getMonth() - (p.from.getFullYear() * 12 + p.from.getMonth()) + 1;
}

// ---------- Aggregationen ----------
export function monthlyRows(entries: Eintrag[]): MonthRow[] {
  if (!entries.length) return [];
  const m = new Map<number, { einsatz: number; gewinn: number; n: number }>();
  for (const e of entries) {
    const d = new Date(e.iso);
    const k = d.getFullYear() * 12 + d.getMonth();
    const cur = m.get(k) ?? { einsatz: 0, gewinn: 0, n: 0 };
    cur.einsatz += e.einsatz;
    cur.gewinn += e.gewinn;
    cur.n++;
    m.set(k, cur);
  }
  const keys = [...m.keys()];
  const lo = Math.min(...keys);
  const hi = Math.max(...keys);
  const rows: MonthRow[] = [];
  for (let k = lo; k <= hi; k++) {
    const y = Math.floor(k / 12);
    const mo = k % 12;
    const v = m.get(k) ?? { einsatz: 0, gewinn: 0, n: 0 };
    rows.push({
      label: `${MON[mo]} ${String(y).slice(2)}`,
      einsatz: v.einsatz,
      gewinn: v.gewinn,
      netto: v.gewinn - v.einsatz,
      n: v.n,
    });
  }
  return rows;
}

export function groupRows(entries: Eintrag[], mode: GroupMode): GroupRow[] {
  if (!entries.length) return [];
  const m = new Map<string, GroupRow & { sort: string }>();
  for (const e of entries) {
    const d = new Date(e.iso);
    let k: string;
    let lbl: string;
    let sort: string;
    if (mode === 'tag') {
      k = isoDay(d);
      lbl = `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
      sort = k;
    } else if (mode === 'woche') {
      const on = new Date(d);
      const day = (on.getDay() + 6) % 7;
      on.setDate(on.getDate() - day);
      k = isoDay(on);
      lbl = `KW ${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
      sort = k;
    } else if (mode === 'jahr') {
      k = String(d.getFullYear());
      lbl = k;
      sort = k;
    } else {
      k = `${d.getFullYear()}-${d.getMonth()}`;
      lbl = `${MON[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
      sort = `${d.getFullYear()}${String(d.getMonth()).padStart(2, '0')}`;
    }
    const cur = m.get(k) ?? { label: lbl, einsatz: 0, gewinn: 0, netto: 0, n: 0, sort };
    cur.einsatz += e.einsatz;
    cur.gewinn += e.gewinn;
    cur.n++;
    cur.netto = cur.gewinn - cur.einsatz;
    m.set(k, cur);
  }
  return [...m.values()]
    .sort((a, b) => (a.sort < b.sort ? -1 : 1))
    .map(({ sort: _s, ...r }) => r);
}

export function artRows(entries: Eintrag[]): ArtRow[] {
  const groups = new Map<Spielart, Eintrag[]>();
  for (const e of entries) {
    const arr = groups.get(e.spielart) ?? [];
    arr.push(e);
    groups.set(e.spielart, arr);
  }
  return [...groups.entries()]
    .map(([spielart, list]) => ({ spielart, ...kpi(list) }))
    .sort((a, b) => b.einsatz - a.einsatz);
}

// ---------- Series (chronologische Folge-Statistiken) ----------
export function computeSeries(chron: Eintrag[]): SeriesResult {
  let noPay = 0;
  let cNoPay = 0;
  let noNet = 0;
  let cNoNet = 0;
  let withPay = 0;
  let cWith = 0;
  let posCount = 0;
  const payDates: Date[] = [];
  for (const e of chron) {
    const net = e.gewinn - e.einsatz;
    if (e.gewinn > 0) {
      cNoPay = 0;
      cWith++;
      if (cWith > withPay) withPay = cWith;
      payDates.push(new Date(e.iso));
    } else {
      cNoPay++;
      if (cNoPay > noPay) noPay = cNoPay;
      cWith = 0;
    }
    if (net > 0) {
      posCount++;
      cNoNet = 0;
    } else {
      cNoNet++;
      if (cNoNet > noNet) noNet = cNoNet;
    }
  }
  let avgGap: number | null = null;
  if (payDates.length > 1) {
    let s = 0;
    for (let i = 1; i < payDates.length; i++) s += Math.abs(payDates[i].getTime() - payDates[i - 1].getTime()) / 86400000;
    avgGap = s / (payDates.length - 1);
  }
  return { noPay, noNet, withPay, posCount, avgGap };
}

// ---------- Wochentage, Tageszeit, Kalendermonate ----------
export interface PatternBucket {
  label: string;
  entries: Eintrag[];
}

export function weekdayBuckets(entries: Eintrag[]): PatternBucket[] {
  const buckets: PatternBucket[] = WD.map((w) => ({ label: w, entries: [] }));
  for (const e of entries) {
    const d = (new Date(e.iso).getDay() + 6) % 7;
    buckets[d].entries.push(e);
  }
  return buckets;
}

export function timeBuckets(entries: Eintrag[]): PatternBucket[] {
  const ranges: { label: string; lo: number; hi: number }[] = [
    { label: '00–06 Uhr', lo: 0, hi: 6 },
    { label: '06–12 Uhr', lo: 6, hi: 12 },
    { label: '12–18 Uhr', lo: 12, hi: 18 },
    { label: '18–24 Uhr', lo: 18, hi: 24 },
  ];
  const buckets: PatternBucket[] = ranges.map(({ label }) => ({ label, entries: [] }));
  for (const e of entries) {
    const hh = new Date(e.iso).getHours();
    for (const b of ranges) {
      if (hh >= b.lo && hh < b.hi) {
        const bucket = buckets.find((x) => x.label === b.label)!;
        bucket.entries.push(e);
        break;
      }
    }
  }
  return buckets;
}

export function monthOfYearBuckets(entries: Eintrag[]): PatternBucket[] {
  const buckets: PatternBucket[] = MON.map((m) => ({ label: m, entries: [] }));
  for (const e of entries) buckets[new Date(e.iso).getMonth()].entries.push(e);
  return buckets;
}
